import React, { useEffect, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { fetchStaffTeam, inviteStaffMember, resendStaffSetupLink, type StaffMember } from '../../utils/staffTeam';
import type { StaffRole } from '../../utils/staffAuth';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from '../../components/portal/adminStyles';

/**
 * Portal Productions staff accounts. Owners only — the list and the invitation
 * both come from the staff-team Edge Function, which re-checks the role
 * server-side, so hiding the page is presentation rather than protection.
 *
 * TWO FIXES, 16 Oct 2026.
 *
 * **The back link is gone.** A "← Client Projects" button sat above the
 * title, on a page reached from the top-level nav — so it offered to take
 * you back somewhere you had not been. Every other top-level tab goes
 * straight to its heading; this one now does too.
 *
 * **Addresses are no longer uppercased.** They were set in `label-sm`,
 * which tracks out and capitalises whatever it is handed, so the page
 * rendered TOM@PORTALPRODUCTIONS.ME. That is not what the address is, and
 * letter-spaced caps are the worst possible setting for a string somebody
 * reads character by character to retype. `value-sm` is the same size with
 * the casing and the tracking left alone — caps are for labels, never for
 * data.
 *
 * **`embedded`, 5 Nov 2026.** This page is now also a section of
 * pages/portal/Settings.tsx, which has its own h1 and its own section
 * heading. The prop suppresses only the title — everything else, including
 * the owners-only message, is identical in both places, because a second
 * copy of the invitation flow was never worth writing.
 */
export function Team({
  embedded = false
}: {
  embedded?: boolean;
} = {}) {
  const {
    staffUser,
    notify
  } = usePortal();
  const [members, setMembers] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<StaffRole>('staff');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState('');
  async function load() {
    setLoading(true);
    const result = await fetchStaffTeam();
    if (!result.ok) setLoadError(result.error ?? 'The team could not be loaded.');else {
      setLoadError('');
      setMembers(result.members ?? []);
    }
    setLoading(false);
  }
  useEffect(() => {
    void load();
  }, []);
  async function resend(address: string) {
    if (resending) return;
    setResending(address);
    const result = await resendStaffSetupLink(address);
    setResending('');
    notify(result.ok ? `New setup link sent to ${address}` : 'Setup link could not be sent.');
  }
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    const address = email.trim().toLowerCase();
    const result = await inviteStaffMember(address, role);
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'Invitation could not be sent.');
      return;
    }
    notify(`Invitation sent to ${address}`);
    setEmail('');
    setRole('staff');
    setOpen(false);
    void load();
  }
  if (staffUser?.role !== 'owner') {
    return <section aria-labelledby={embedded ? undefined : 'team'} data-id="element-2528" data-mp="yqcj3amzvqlv">
        {!embedded && <h1 id="team" className="font-display text-4xl leading-[0.95] tracking-tight sm:text-6xl" data-id="element-2529" data-mp="12r6cgjj7hxm5">
            Team
          </h1>}
        <p className={`font-display text-xl text-paper/40 ${embedded ? 'mt-0' : 'mt-8'}`} data-id="element-2530" data-mp="1cqncakbs1ha7">
          Only Portal owners can manage staff accounts.
        </p>
        <p className="label-sm mt-5 text-paper/30" data-id="element-2531" data-mp="39ki3z6sp2nsg">
          Everything else in Portal is the same for you as for an owner.
        </p>
      </section>;
  }
  return <section aria-labelledby={embedded ? undefined : 'team'} data-id="element-2532" data-mp="18syevk0bc3gb">
      {!embedded && <h1 id="team" className="font-display text-4xl leading-[0.95] tracking-tight sm:text-6xl" data-id="element-2533" data-mp="13v6cfw21kyxv">
          Team
        </h1>}

      <ul className={`m-0 list-none p-0 ${embedded ? 'mt-0 border-t border-ruleDark' : 'mt-12'}`} data-id="element-2534" data-mp="1ht4mwqd8sb83">
        {loading && <li className="label border-b border-ruleDark py-8 text-paper/35" data-id="element-2535" data-mp="1dqq589lb7hie">
            Loading team…
          </li>}

        {!loading && loadError && <li className="label border-b border-ruleDark py-8 text-paper/35" data-id="element-2536" data-mp="l5gj7ddxgl82">
            {loadError}
          </li>}

        {!loading && !loadError && members.map((member, index) => <li key={member.userId} style={{
        ['--row' as string]: Math.min(index, 10)
      }} className="row-in flex flex-wrap items-baseline justify-between gap-x-8 gap-y-3 border-b border-ruleDark py-7" data-id="element-2537" data-mp="u3k5ibwqlm0x" data-mpi={index}>
              <div className="min-w-0" data-id="element-2538" data-mp="3oelfo3yjolus">
                <p className="m-0 font-display text-2xl leading-none" data-id="element-2539" data-mp="3odts9gf3pufl">{member.name}</p>
                <p className="value-sm mt-3 text-paper/30" data-id="element-2540" data-mp="3t6mtvvf9wmng">{member.email}</p>
              </div>
              <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2" data-id="element-2541" data-mp="3ei9wcu0jevqu">
                {member.pending && <>
                    <span className="label-sm text-paper/30" data-id="element-2542" data-mp="3jdp7vfb2ofk3">Invited</span>
                    {/* Presentation only — the function re-checks the role. */}
                    <button type="button" onClick={() => resend(member.email)} disabled={resending === member.email} className="label-sm text-paper/35 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper disabled:text-paper/20 disabled:hover:text-paper/20" data-id="element-2543" data-mp="fy0cx4wiap04">
                      Resend Setup Link
                    </button>
                  </>}
                <span className="label text-paper/50" data-id="element-2544" data-mp="x847s0afpj9k">{member.role}</span>
              </div>
            </li>)}
      </ul>

      {open ? <form onSubmit={submit} className={`max-w-xl ${embedded ? 'mt-9' : 'mt-12'}`} data-id="element-2545" data-mp="nikoitit7ne6">
          <p className="label m-0 text-paper/40" data-id="element-2546" data-mp="nghrvqn5iw0h">Invite Team Member</p>

          <label className="mt-9 block" data-id="element-2547" data-mp="ijhip6v09d55">
            <span className="label block text-paper/40" data-id="element-2548" data-mp="dkepqksauxdj">Email</span>
            <input type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="e.g. name@portalproductions.me" autoFocus className={FIELD} data-id="element-2549" data-mp="dik2v0xgwv6t" />
          </label>

          <label className="mt-9 block" data-id="element-2550" data-mp="3vs4367czdsx8">
            <span className="label block text-paper/40" data-id="element-2551" data-mp="1yxr53vfosrtc">Role</span>
            <select value={role} onChange={event => setRole(event.target.value as StaffRole)} className={SELECT} data-id="element-2552" data-mp="1yvwvveds2o53">
              <option value="staff" className={OPTION} data-id="element-2553" data-mp="21hnvxg4cu5lm">
                Staff
              </option>
              <option value="owner" className={OPTION} data-id="element-2554" data-mp="26g7gbc3av9bv">
                Owner
              </option>
            </select>
          </label>

          {error && <p role="alert" className="label-sm mt-8 text-paper" data-id="element-2555" data-mp="2g7v32zoww270">
              {error}
            </p>}

          <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-2556" data-mp="1ikct5ljiku1f">
            <button type="submit" disabled={busy} className={BUTTON_SOLID} data-id="element-2557" data-mp="1nio2zw9t74jn">
              Send Invitation
            </button>
            <button type="button" onClick={() => setOpen(false)} className={BUTTON_QUIET} data-id="element-2558" data-mp="1shhitvckj7mz">
              Cancel
            </button>
          </div>
        </form> : <button type="button" onClick={() => setOpen(true)} className={`${BUTTON_GHOST} inline-flex items-center gap-3 ${embedded ? 'mt-9' : 'mt-12'}`} data-id="element-2559" data-mp="33x684tdaoiqy">
          <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-2560" data-mp="3u01ha9i9u2nw" />
          Invite Team Member
        </button>}
    </section>;
}